/**
 * Product Variant Cards - Integrates with existing color drawer system
 */
class ProductVariantCards extends HTMLElement {
  constructor() {
    super();
    setTimeout(() => this.initialize(), 100);
  }
  
  initialize() {
    const buttons = this.querySelectorAll('.variant-card__button');
    
    if (buttons.length === 0) {
      setTimeout(() => this.initialize(), 200);
      return;
    }
    
    buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const variantId = button.dataset.variantId;
        const colourDrawer = document.querySelector(`.variant-cards-drawer--${variantId}`);
        
        if (colourDrawer) {
          this.overrideCheckoutRedirect(colourDrawer);
          
          if (typeof colourDrawer.toggleDrawer === 'function') {
            colourDrawer.toggleDrawer(event);
            
            // Restore colors after drawer opens
            setTimeout(() => {
              if (typeof colourDrawer.restoreColorsForCurrentColorsCount === 'function') {
                colourDrawer.restoreColorsForCurrentColorsCount();
              }
            }, 150);
          }
        }
      });
    });
  }

  overrideCheckoutRedirect(drawer) {
    if (drawer.dataset.checkoutOverridden) return;
    drawer.dataset.checkoutOverridden = 'true';
    
    const checkoutBtn = drawer.querySelector('.drawer__atc-button, .drawer__checkout-button');
    if (!checkoutBtn) return;

    const checkoutButtonText = this.dataset.checkoutButtonText || 'Proceed to Checkout';
    checkoutBtn.textContent = checkoutButtonText;
    
    // Remove existing event listeners to prevent duplicates
    const newCheckoutBtn = checkoutBtn.cloneNode(true);
    checkoutBtn.parentNode.replaceChild(newCheckoutBtn, checkoutBtn);
    
    newCheckoutBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const formDataDiv = drawer.querySelector('.nail-set__form-data');
      if (!formDataDiv) {
        this.showError('Could not find product form data. Please try again.');
        return;
      }

      // Validate color selection
      const colorInputs = formDataDiv.querySelectorAll('input[id^="id_"][class="form-input"]');
      const hasSelectedColors = Array.from(colorInputs).some(input => input.value);
      
      if (!hasSelectedColors) {
        this.showError('Please select your colors first.');
        return;
      }

      this.addToCart(formDataDiv, newCheckoutBtn);
    });
  }

  addToCart(formDataDiv, checkoutBtn) {
    const originalText = checkoutBtn.textContent;
    checkoutBtn.textContent = 'Adding to Cart...';
    checkoutBtn.disabled = true;

    try {
      const mainVariantId = this.getMainVariantId(formDataDiv);
      const formData = this.buildCartFormData(formDataDiv, mainVariantId);
      
      this.submitToCart(formData, mainVariantId)
        .then(() => {
          // Give more time for cart processing and free gift evaluation
          setTimeout(() => {
            window.location.href = '/checkout';
          }, 500);
        })
        .catch(error => {
          console.error('Cart add error:', error);
          this.showError('Error adding to cart. Please try again.');
        })
        .finally(() => {
          this.resetButtonState(checkoutBtn, originalText);
        });

    } catch (error) {
      console.error('Error preparing cart data:', error);
      this.showError('Error adding to cart. Please try again.');
      this.resetButtonState(checkoutBtn, originalText);
    }
  }

  getMainVariantId(formDataDiv) {
    const mainVariantInput = formDataDiv.querySelector('#main_variant_id');
    const mainVariantId = mainVariantInput?.value;
    
    if (!mainVariantId) {
      throw new Error('Main variant ID not found');
    }
    
    return mainVariantId;
  }

  buildCartFormData(formDataDiv, mainVariantId) {
    const formData = new FormData();
    const qtyId = Math.random().toString(36).substring(2, 7);
    let itemIndex = 1;

    // Build groupId as variantId_Color1_Color2_... to match the regular colour-drawer format.
    // This makes the line-colour-changer icon appear on cart line items.
    const colorTitleInputs = formDataDiv.querySelectorAll('input[name^="properties[Option"]');
    let groupId = mainVariantId;
    colorTitleInputs.forEach(input => {
      if (input.value) {
        groupId = `${groupId}_${input.value}`;
      }
    });

    // Add selected colors
    itemIndex = this.addColorItems(formData, formDataDiv, groupId, qtyId, itemIndex);
    
    // Add free addon products
    itemIndex = this.addFreeProducts(formData, formDataDiv, groupId, qtyId, itemIndex);
    
    // Add main product
    this.addMainProduct(formData, formDataDiv, mainVariantId, groupId, qtyId, itemIndex);

    // Add cart sections for drawer update
    const cart = document.querySelector('cart-drawer');
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map(section => section.section));
      formData.append('sections_url', window.location.pathname);
    }

    return formData;
  }

  addColorItems(formData, formDataDiv, groupId, qtyId, startIndex) {
    const colorInputs = formDataDiv.querySelectorAll('input[id^="id_"][class="form-input"]');
    const drawerEl = formDataDiv.closest('colour-drawer');
    let itemIndex = startIndex;
    
    // Convert to array and reverse to maintain proper color order
    Array.from(colorInputs).reverse().forEach((input) => {
      if (input.value) {
        this.addCartItem(formData, itemIndex, input.value, 'subitem', groupId, qtyId);

        // Mark free colour slots
        const colourIndex = input.dataset.colourIndex;
        const colourSelector = drawerEl?.querySelector(`.colour-selectors[data-slot-index="${colourIndex}"]`);
        if (colourSelector?.classList.contains('free_colour')) {
          formData.append(`items[${itemIndex}][properties][_free_color]`, 'true');
        }

        itemIndex++;
      }
    });
    
    return itemIndex;
  }

  addFreeProducts(formData, formDataDiv, groupId, qtyId, startIndex) {
    const freeProductInputs = formDataDiv.querySelectorAll('input[id^="free_product_id_"]');
    let itemIndex = startIndex;
    
    freeProductInputs.forEach((input) => {
      if (input.value) {
        this.addCartItem(formData, itemIndex, input.value, 'subitem', groupId, qtyId);
        itemIndex++;
      }
    });
    
    return itemIndex;
  }

  addMainProduct(formData, formDataDiv, mainVariantId, groupId, qtyId, itemIndex) {
    // Add main product
    this.addCartItem(formData, itemIndex, mainVariantId, 'main', groupId, qtyId);
    
    // Add color properties to main product (Option 1, Option 2, etc.)
    const colorTitleInputs = formDataDiv.querySelectorAll('input[name^="properties[Option"]');
    colorTitleInputs.forEach(input => {
      if (input.value) {
        const propertyName = input.name.replace('properties[', '').replace(']', '');
        formData.append(`items[${itemIndex}][properties][${propertyName}]`, input.value);
      }
    });

    // Add discount codes if present
    this.addDiscountCodes(formData, formDataDiv, itemIndex);
  }

  addCartItem(formData, itemIndex, variantId, itemType, groupId, qtyId) {
    formData.append(`items[${itemIndex}][id]`, variantId);
    formData.append(`items[${itemIndex}][quantity]`, '1');
    formData.append(`items[${itemIndex}][properties][_item_type]`, itemType);
    formData.append(`items[${itemIndex}][properties][_group_id]`, groupId);
    formData.append(`items[${itemIndex}][properties][_qtyId]`, qtyId);
    
    // Each color gets unique randomId to prevent combining same colors
    if (itemType === 'subitem') {
      formData.append(`items[${itemIndex}][properties][_randomId]`, Math.random().toString(36).substring(2, 7));
    }
  }

  addDiscountCodes(formData, formDataDiv, itemIndex) {
    const discountInput = formDataDiv.querySelector('input[name="properties[_discount_code]"]');
    if (discountInput?.value) {
      formData.append(`items[${itemIndex}][properties][_discount_code]`, discountInput.value);
    }

    const discountProductInput = formDataDiv.querySelector('input[name="properties[_discount_code_product]"]');
    if (discountProductInput?.value) {
      formData.append(`items[${itemIndex}][properties][_discount_code_product]`, discountProductInput.value);
    }
  }

  async submitToCart(formData, mainVariantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    
    if (responseData.status) {
      throw new Error(responseData.description || responseData.message || 'Error adding to cart');
    }

    // Sequential processing to match original working version
    this.updateCartDrawer(responseData);
    await this.triggerFreeGiftLogic();
    this.publishCartEvents(responseData, mainVariantId);

    return responseData;
  }

  updateCartDrawer(data) {
    if (!data.sections) return;
    
    const cart = document.querySelector('cart-drawer');
    if (!cart) return;

    cart.getSectionsToRender().forEach(section => {
      const elementToReplace = document.querySelector(section.selector) || document.querySelector(section.id);
      if (elementToReplace && data.sections[section.section]) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(data.sections[section.section], section.selector);
      }
    });
  }

  showError(message) {
    alert(message);
  }

  resetButtonState(button, originalText) {
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 500); // Optimized delay
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  publishCartEvents(responseData, mainVariantId) {
    if (!window.PUB_SUB_EVENTS || !window.publish) return Promise.resolve();

    const mainProduct = responseData.items?.find(item => item.properties?._item_type === 'main');
    
    // Initial cart update event (triggers free gift logic)
    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
      source: "product-form-sets",
      productVariantId: mainVariantId,
      cartData: responseData
    });

    // Product added event if main product exists
    if (mainProduct) {
      window.publish(window.PUB_SUB_EVENTS.productAdded, {
        source: "product-form-sets",
        productVariantId: mainVariantId,
        cartData: responseData,
        detail: {
          variant: mainProduct,
          quantity: mainProduct.quantity
        }
      });
    }

    // Additional events that might trigger free gift logic
    setTimeout(() => {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: "product-form-sets",
        productVariantId: mainVariantId,
        cartData: responseData
      });
    }, 100);
    
    return Promise.resolve();
  }

  async triggerFreeGiftLogic() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.updateQuantity) {
        await cartDrawer.updateQuantity();
      }
      await this.sleep(300);
      return true;
    } catch (error) {
      console.warn('Cart drawer validation failed:', error);
      await this.sleep(400);
      return false;
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

customElements.define('product-variant-cards', ProductVariantCards);